'use client';

import React, { useState } from 'react';
import { Sparkles, ArrowRight, Mic } from 'lucide-react';
import { useCorporate } from '@/context/CorporateContext';

/**
 * Apple iOS 26 Ultra Liquid Glass Ambient Background for Next.js Web Portals & Wallet
 * Dynamically refracts --tenant-primary and --tenant-secondary CSS tokens with
 * diagonal architectural light beams and champagne/cobalt bokeh spheres.
 */
export function LiquidAmbientBackground({
  fixed = true,
  intensity = 'default',
}: {
  fixed?: boolean;
  intensity?: 'subtle' | 'default' | 'vibrant';
}) {
  const opacityClass =
    intensity === 'vibrant'
      ? 'opacity-55'
      : intensity === 'subtle'
      ? 'opacity-35'
      : 'opacity-45';

  return (
    <div
      aria-hidden="true"
      className={`${fixed ? 'fixed' : 'absolute'} inset-0 overflow-hidden pointer-events-none z-0`}
    >
      {/* Diagonal Architectural Window Beam 1 */}
      <div
        className="absolute -top-[25%] -left-[10%] w-[68vw] h-[145vh] -rotate-[28deg] blur-[46px] opacity-65 transition-all duration-700"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.62) 45%, color-mix(in srgb, var(--tenant-primary, #136dec) 26%, rgba(255, 255, 255, 0.72)) 54%, transparent 100%)',
        }}
      />
      {/* Diagonal Architectural Window Beam 2 */}
      <div
        className="absolute -top-[18%] -right-[12%] w-[56vw] h-[135vh] rotate-[32deg] blur-[52px] opacity-55 transition-all duration-700"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--tenant-secondary, #3b82f6) 24%, rgba(255, 248, 231, 0.65)) 50%, transparent 100%)',
        }}
      />
      {/* Top-Left Primary Refractive Orb */}
      <div
        className={`absolute -top-28 -left-24 w-[480px] h-[480px] rounded-full blur-[95px] ${opacityClass} transition-colors duration-700`}
        style={{
          background:
            'radial-gradient(circle, var(--tenant-primary, #136dec) 0%, transparent 70%)',
        }}
      />
      {/* Center-Right Secondary + Warm Champagne Refractive Orb */}
      <div
        className={`absolute top-1/4 -right-24 w-[460px] h-[460px] rounded-full blur-[105px] ${opacityClass} transition-colors duration-700`}
        style={{
          background:
            'radial-gradient(circle, var(--tenant-secondary, #3b82f6) 0%, rgba(251, 191, 36, 0.32) 48%, transparent 72%)',
        }}
      />
      {/* Bottom-Left Deep Chromatic Orb */}
      <div
        className={`absolute -bottom-32 left-1/4 w-[500px] h-[500px] rounded-full blur-[115px] ${opacityClass} transition-colors duration-700`}
        style={{
          background:
            'radial-gradient(circle, var(--tenant-primary, #136dec) 0%, transparent 72%)',
        }}
      />
    </div>
  );
}

/**
 * Top Spatial Omnibar for Max Copilot in Merchant Suite & Backoffice Master.
 * Embedded in the Top Header Bar (Reference Images 1 & 2) and connected via
 * 'konex:max-copilot-prompt' to the bottom-left MaxFloatingCopilot bubble.
 */
export function MaxTopSpatialOmnibar({
  portalMode = 'merchant',
  placeholder,
}: {
  portalMode?: 'merchant' | 'master';
  placeholder?: string;
}) {
  const { config } = useCorporate();
  const [promptText, setPromptText] = useState('');

  const primaryHex = config?.hexPrimary || '#136dec';
  const defaultPlaceholder =
    portalMode === 'master'
      ? 'Pedile a Max: "Ver empresas activas", "Revisar conciliación", "Auditar alertas"...'
      : 'Pedile a Max: "Cobrar con QR", "Simular adelanto de ventas", "Descargar resumen"...';

  const handleTriggerMax = (e?: React.FormEvent, customPrompt?: string) => {
    if (e) e.preventDefault();
    const finalPrompt = (customPrompt !== undefined ? customPrompt : promptText).trim();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('konex:max-copilot-prompt', {
          detail: {
            prompt: finalPrompt,
            openDrawer: true,
          },
        })
      );
    }
    if (customPrompt === undefined) {
      setPromptText('');
    }
  };

  return (
    <form
      onSubmit={(e) => handleTriggerMax(e)}
      className="relative flex-1 max-w-2xl mx-auto w-full"
      role="search"
      aria-label="Barra superior de conversación con Max"
    >
      <div className="glass-pill-obsidian flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-white/90 dark:border-white/20 shadow-[inset_0_2px_1.5px_rgba(255,255,255,0.98),0_12px_28px_-8px_rgba(15,23,42,0.12)] transition-all focus-within:ring-2 focus-within:ring-sky-500/30">
        <button
          type="button"
          onClick={() => handleTriggerMax(undefined, '')}
          title="Abrir asistente Max"
          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform hover:scale-105 cursor-pointer"
          style={{
            background: `linear-gradient(135deg, ${primaryHex} 0%, var(--tenant-secondary, #0284c7) 100%)`,
          }}
        >
          <Sparkles className="h-4 w-4" />
        </button>

        <input
          type="text"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          placeholder={placeholder || defaultPlaceholder}
          aria-label="Escribile una consulta o acción a Max"
          className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 dark:text-white placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none"
        />

        <button
          type="button"
          onClick={() => handleTriggerMax(undefined, '')}
          title="Dictar o abrir conversación con Max"
          className="hidden sm:flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/60 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-white/80 dark:border-white/15 transition cursor-pointer"
        >
          <Mic className="h-3.5 w-3.5" />
        </button>

        <button
          type="submit"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-black text-white shadow-sm transition-all hover:opacity-95 active:scale-95 cursor-pointer"
          style={{
            background: `linear-gradient(135deg, ${primaryHex} 0%, var(--tenant-secondary, #0284c7) 100%)`,
          }}
        >
          <span>Preguntar a Max</span>
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </form>
  );
}

export function LiquidGlassCard({
  children,
  variant = 'default',
  className = '',
  onClick,
  style,
}: {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'tint';
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}) {
  const baseVariant =
    variant === 'elevated'
      ? 'liquid-glass-elevated'
      : variant === 'tint'
      ? 'liquid-glass-tint'
      : 'liquid-glass';

  return (
    <div
      onClick={onClick}
      style={style}
      className={`relative overflow-hidden rounded-[1.75rem] ${baseVariant} ${
        onClick ? 'cursor-pointer transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]' : ''
      } ${className}`}
    >
      {/* Top specular catch-light rim */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export function LiquidGlassButton({
  children,
  onClick,
  disabled,
  type = 'button',
  variant = 'primary',
  className = '',
  style,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'glass' | 'danger';
  className?: string;
  style?: React.CSSProperties;
}) {
  const variantStyles: React.CSSProperties =
    variant === 'primary'
      ? {
          background: 'var(--tenant-gradient, var(--tenant-primary, #136dec))',
          color: 'var(--tenant-gradient-text, #ffffff)',
          ...style,
        }
      : variant === 'danger'
      ? {
          background: 'rgba(239, 68, 68, 0.18)',
          borderColor: 'rgba(239, 68, 68, 0.45)',
          color: '#fca5a5',
          ...style,
        }
      : {
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
          color: '#ffffff',
          ...style,
        };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={variantStyles}
      className={`liquid-glass-button rounded-full font-bold tracking-wide disabled:opacity-50 disabled:pointer-events-none ${className}`}
    >
      {/* Top half liquid lens reflection */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent"
      />
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
    </button>
  );
}
